import type {
  ListAuditQuery,
  ListLessonEarningsQuery,
  ListSessionsQuery,
  ListSubscriptionsQuery,
  ListTrialsQuery,
  ListTutorApplicationsQuery,
  ListUsersQuery,
  SearchTutorsQuery,
} from '../types/api';

/**
 * Every cache key in one place, so an invalidation after a mutation can
 * never silently miss a list because the key was spelled differently at the
 * two call sites.
 */
export const qk = {
  website: ['admin', 'website'] as const,
  websiteHistory: (id: string) => ['admin', 'website', 'history', id] as const,
  websiteCatalog: ['admin', 'website-catalog'] as const,
  support: ['admin', 'support'] as const,
  supportUnread: ['admin', 'support', 'unread'] as const,
  supportInbox: (offset: number) => ['admin', 'support', 'inbox', offset] as const,
  supportMessages: (id: string) => ['admin', 'support', 'messages', id] as const,
  replacementCandidates: (id: string, target: object) => ['admin', 'learning-plans', 'replacement-candidates', id, target] as const,
  teacherCandidates: (id: string) => ['admin', 'learning-plans', 'teacher-candidates', id] as const,
  adjustmentPreview: (id: string, input: object) => ['admin', 'learning-plans', 'adjustment-preview', id, input] as const,
  adjustmentCandidates: (id: string, input: object) => ['admin', 'learning-plans', 'adjustment-candidates', id, input] as const,
  learningPlans: ['admin', 'learning-plans'] as const,
  offerCatalog: (country: string) => ['catalog', 'plan-offer', country] as const,
  health: ['health'] as const,

  me: ['me'] as const,
  myTutorProfile: ['me', 'tutor'] as const,
  myStudentProfile: ['me', 'student'] as const,

  stats: ['admin', 'stats'] as const,

  users: (query: ListUsersQuery) => ['admin', 'users', query] as const,
  usersAll: ['admin', 'users'] as const,
  user: (id: string) => ['admin', 'users', 'detail', id] as const,
  userSessions: (id: string) => ['admin', 'users', 'detail', id, 'sessions'] as const,

  adminSessions: (query: ListSessionsQuery) => ['admin', 'sessions', query] as const,
  adminSessionsAll: ['admin', 'sessions'] as const,
  adminSession: (id: string) => ['admin', 'sessions', 'detail', id] as const,
  transcript: (id: string) => ['admin', 'sessions', 'transcript', id] as const,

  audit: (query: ListAuditQuery) => ['admin', 'audit', query] as const,
  auditAll: ['admin', 'audit'] as const,

  mySessions: (query: ListSessionsQuery) => ['sessions', query] as const,
  mySessionsAll: ['sessions'] as const,
  session: (id: string) => ['sessions', 'detail', id] as const,
  messages: (id: string) => ['sessions', 'messages', id] as const,

  tutors: (query: SearchTutorsQuery) => ['tutors', query] as const,
  tutor: (id: string) => ['tutors', 'detail', id] as const,
  myAvailability: (query: Record<string, unknown>) => ['availability', 'me', query] as const,
  myAvailabilityAll: ['availability', 'me'] as const,
  tutorAvailability: (id: string) => ['availability', id] as const,

  trialBundles: ['admin', 'trial-bundles'] as const,
  trialPipeline: ['admin', 'trials', 'pipeline'] as const,
  trials: (query: ListTrialsQuery) => ['admin', 'trials', query] as const,
  trialsAll: ['admin', 'trials'] as const,
  trial: (id: string) => ['admin', 'trials', 'detail', id] as const,

  subscriptions: (query: ListSubscriptionsQuery) =>
    ['admin', 'subscriptions', query] as const,
  subscriptionsAll: ['admin', 'subscriptions'] as const,
  subscription: (id: string) => ['admin', 'subscriptions', 'detail', id] as const,

  // Tutor registration and review (spec 32).
  tutorApplications: (query: ListTutorApplicationsQuery) =>
    ['admin', 'tutor-applications', query] as const,
  tutorApplicationsAll: ['admin', 'tutor-applications'] as const,
  tutorApplication: (id: string) =>
    ['admin', 'tutor-applications', 'detail', id] as const,
  tutorCommissions: (id: string) => ['admin', 'tutors', id, 'commissions'] as const,
  /**
   * Scoped by whether the number was revealed: a masked read and an audited
   * reveal are different answers, and caching them under one key would serve
   * a plaintext IBAN to a later caller who never asked for one.
   */
  tutorPayoutAccount: (id: string, reveal: boolean) =>
    ['admin', 'tutors', id, 'payout-account', reveal] as const,
  tutorPayoutAccountAll: (id: string) => ['admin', 'tutors', id, 'payout-account'] as const,
  lessonEarnings: (query: ListLessonEarningsQuery) =>
    ['admin', 'lesson-earnings', query] as const,
  lessonEarningsAll: ['admin', 'lesson-earnings'] as const,
};
